import { createContext, useContext, useState } from "react";
import { useDashboardSourceDataContext } from "../DashboardSourceDataProvider";
import { useSharedData } from "../hooks/useSharedData";

export const VisualExportContext = createContext();

export const VisualExportContextProvider = ({ children }) => {

    const { getCachedDataFromVisual } = useDashboardSourceDataContext();

    const {
        datasets
    } = useSharedData();

    const [exportVisual, setExportVisual] = useState();
    const [selectedType, setSelectedType] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    const exportVisualExportName = exportVisual?.name ?? exportVisual?.visualId ?? "unknown";

    const exportData = async (selectedType, friendlyData, exportVisualExportName)=>{
        console.log("exportData: ",selectedType, friendlyData, exportVisualExportName);
    }

    const resetState = () => {
        setExportVisual(null);
        setSelectedType(null);
        setExportLoading(false);
    }

    const openExportDialog = (visual) => {
        setExportVisual(visual);
    };

    const closeExportDialog = () => {
        resetState();
    };

    const chooseType = (type) => {
        setSelectedType(type);
    }

    const exportVisualImage = () => {

        if (!exportVisual) {
            console.error("Visual not selected");
            return;
        }

        const parentElement = document.getElementsByClassName(`visual-${exportVisual.visualId ?? 'unknown'}`)?.[0];
        const canvas = parentElement?.querySelector('canvas');

        if (!canvas) {
            console.error("Canvas not found");
            return;
        }

        canvas.toBlob((blob) => {
            if (!blob) {
                console.error("Failed to create canvas or blob");
                resetState();
                return;
            }

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${exportVisualExportName}-${new Date().getTime()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            resetState();
        }, 'image/png');
    }

    const exportVisualData = () => {
        setExportLoading(true);

        const data = getCachedDataFromVisual(exportVisual);
        const friendlyNameMap = datasets
            .find(x => x.key === exportVisual.datasetNameA)
            .columns.reduce((r, d) => {
                r[d.key] = d.label;

                return r;
            }, {});

        const friendlyData = data.reduce((r, e) => {
            const newData = {};
            for (const [key, value] of Object.entries(e)) {
                newData[friendlyNameMap[key]] = value;
            }
            r.push(newData)
            return r;
        }, []);

        exportData(selectedType, friendlyData, exportVisualExportName)
            .then(() => {
                console.log("Export completed");
            })
            .finally(() => {
                resetState();
            })
    }

    const handleExportVisual = () => {
        if (exportVisual.type !== "table" && selectedType === "png") {
            exportVisualImage();
        } else if (selectedType !== "png") {
            exportVisualData();
        } else {
            console.error("Invalid export type for this visual type");
            resetState();
        }
    }

    return (
        <VisualExportContext.Provider
            value={{
                exportVisual,
                selectedType,
                exportLoading,
                openExportDialog,
                closeExportDialog,
                chooseType,
                handleExportVisual
            }}
        >
            {children}
        </VisualExportContext.Provider>
    );
}

export const useVisualExportContext = () => useContext(VisualExportContext);
